package org.example.project.settings

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.roundToInt
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Text

enum class LumaticaButtonVariant { Standard, Action, State, VertriebsAntwort }

private const val LUMATICA_BUTTON_SCALE = 0.78
private const val LUMATICA_BUTTON_MIN_HEIGHT_PX = 28

private object LumaticaButtonStyles : StyleSheet() {

    val base by style {
        backgroundColor(Color.transparent)
        borderRadius(8.px)
        fontSize(12.px)
        fontWeight("600")
        cursor("pointer")
        property("box-sizing", "border-box")
        property("white-space", "nowrap")
        property("transition", "border-color 0.16s ease, box-shadow 0.16s ease, color 0.16s ease, transform 0.12s ease")
        display(DisplayStyle.Flex)
        alignItems(AlignItems.Center)
        justifyContent(JustifyContent.Center)
        gap(6.px)
    }

    // Kein Glow, blasse weiße Border, blasses Weiß-Text
    val standard by style {
        property("border", "1px solid rgba(255,255,255,0.16)")
        property("box-shadow", "0 10px 24px rgba(0,0,0,0.20), inset 0 1px 0 rgba(255,255,255,0.08)")
        color(UISettings.STATE_BUTTON_TEXT)
        hover {
            property("border-color", "rgba(255,255,255,0.30)")
            property("box-shadow", "0 12px 28px rgba(0,0,0,0.26), 0 0 18px rgba(255,255,255,0.08), inset 0 1px 0 rgba(255,255,255,0.12)")
        }
    }

    // Kein Glow im Ruhezustand — weißer Glow leuchtet nur beim Klick auf
    val action by style {
        property("border", "1px solid rgba(255,255,255,0.22)")
        property("box-shadow", "0 12px 28px rgba(0,0,0,0.24), 0 0 14px ${UISettings.accentRgba(0.08)}, inset 0 1px 0 rgba(255,255,255,0.10)")
        color(Color.white)
        hover {
            property("border-color", "rgba(255,255,255,0.36)")
            property("box-shadow", "0 14px 32px rgba(0,0,0,0.30), 0 0 18px rgba(255,255,255,0.10), 0 0 16px ${UISettings.accentRgba(0.10)}, inset 0 1px 0 rgba(255,255,255,0.14)")
        }
    }

    val accentHover by style {
        hover {
            property("border-color", "${UISettings.ACCENT}")
            property("box-shadow", "0 0 0 1px ${UISettings.accentRgba(0.35)}, 0 0 22px ${UISettings.accentRgba(0.36)}")
        }
    }

    val standardPressed by style {
        property("border-color", "rgba(255,255,255,0.34)")
        backgroundColor(rgba(255, 255, 255, 0.055))
        property("box-shadow", "0 0 16px rgba(255,255,255,0.10), 0 0 14px ${UISettings.accentRgba(0.16)}, inset 0 1px 0 rgba(255,255,255,0.12)")
        property("transform", "translateY(1px)")
    }

    val actionPressed by style {
        property("transition", "none")
        property("border-color", UISettings.ACCENT_HEX)
        backgroundColor(Color(UISettings.accentRgba(0.22)))
        property("box-shadow", "0 0 0 1px ${UISettings.accentRgba(0.45)}, 0 0 20px ${UISettings.accentRgba(0.42)}, inset 0 1px 0 rgba(255,255,255,0.18)")
        property("transform", "translateY(1px)")
        hover {
            property("border-color", UISettings.ACCENT_HEX)
            property("box-shadow", "0 0 0 1px ${UISettings.accentRgba(0.45)}, 0 0 20px ${UISettings.accentRgba(0.42)}, inset 0 1px 0 rgba(255,255,255,0.18)")
        }
    }

    // State inaktiv: dunkle Border, kein Glow, blass
    val stateOff by style {
        property("border", "1px solid rgba(255,255,255,0.14)")
        property("box-shadow", "0 10px 24px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.07)")
        color(UISettings.STATE_BUTTON_TEXT)
        hover {
            property("border-color", "rgba(255,255,255,0.28)")
            property("box-shadow", "0 12px 28px rgba(0,0,0,0.24), 0 0 14px rgba(255,255,255,0.07), inset 0 1px 0 rgba(255,255,255,0.11)")
        }
        active {
            property("transform", "translateY(1px)")
        }
    }

    // State aktiv: roter Rahmen mit rotem Glow
    val stateOn by style {
        property("border", "1px solid ${UISettings.ACCENT}")
        property("box-shadow", "0 12px 30px rgba(0,0,0,0.26), 0 0 18px ${UISettings.accentRgba(0.22)}, inset 0 1px 0 rgba(255,255,255,0.14)")
        backgroundColor(rgba(255, 255, 255, 0.06))
        color(UISettings.STATE_BUTTON_TEXT)
        fontWeight("700")
        desc(self, selector("img")) style {
            property("filter", UISettings.STATE_BUTTON_ACTIVE_ICON_FILTER)
        }
        active {
            property("transform", "translateY(1px)")
        }
    }

    val vertriebsAntwort by style {
        backgroundColor(rgba(255, 255, 255, 0.09))
        property("border", "1px solid rgba(255,255,255,0.24)")
        property("box-shadow", "0 8px 18px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.10)")
        color(Color.white)
        fontSize(17.px)
        fontWeight("600")
        property("white-space", "normal")
        property("text-align", "center")
        justifyContent(JustifyContent.Center)
        hover {
            backgroundColor(rgba(255, 255, 255, 0.14))
            property("border-color", "rgba(255,255,255,0.42)")
        }
        active { property("transform", "translateY(1px)") }
    }

    val vertriebsAntwortActive by style {
        backgroundColor(rgba(255, 255, 255, 0.13))
        property("border", "1px solid ${UISettings.ACCENT}")
        property("box-shadow", "0 8px 20px rgba(0,0,0,0.22), 0 0 16px ${UISettings.accentRgba(0.20)}")
    }

    val btnDisabled by style {
        opacity(0.4)
        cursor("default")
        property("pointer-events", "none")
    }

    val driverLoginReference by style {
        fontSize(15.px)
        fontWeight("600")
        borderRadius(8.px)
        backgroundColor(Color.transparent)
        color(Color.white)
        property("border", "1px solid rgba(255,255,255,0.22)")
        property("box-shadow", "0 0 0 1px rgba(255,255,255,0.05), 0 0 18px rgba(255,255,255,0.08)")
    }
    val accessPrimary by style {
        backgroundColor(Color(UISettings.LOGIN_BUTTON_BG))
        color(Color.white)
        property("border", "1px solid ${UISettings.accentRgba(0.72)}")
        property("box-shadow", "0 12px 30px ${UISettings.accentRgba(0.32)}, inset 0 1px 0 rgba(255,255,255,0.18)")
        hover {
            backgroundColor(UISettings.ACCENT)
            property("border-color", "${UISettings.accentRgba(0.9)}")
            property("box-shadow", "0 15px 34px ${UISettings.accentRgba(0.42)}, inset 0 1px 0 rgba(255,255,255,0.22)")
        }
    }
}

/**
 * Einheitlicher Button für das gesamte Lumatica-System.
 *
 * Varianten:
 * - Standard : blasses Weiß, subtile Border, kein Glow
 * - Action   : weißer Glow leuchtet nur beim Klick auf
 * - State    : roter Rahmen mit Glow wenn isActive=true, dunkle Border wenn false
 *
 * Alle Varianten: transparenter Hintergrund, feste Höhe, dynamische Breite.
 */
@Composable
fun LumaticaButton(
    label: String,
    onClick: () -> Unit,
    variant: LumaticaButtonVariant = LumaticaButtonVariant.Standard,
    enabled: Boolean = true,
    isActive: Boolean = false,
    heightPx: Int = 36,
    paddingH: Int = 16,
    fillWidth: Boolean = false,
    justify: JustifyContent = JustifyContent.Center,
    driverLoginReference: Boolean = false,
    accessPrimary: Boolean = false,
    wrapLabel: Boolean = false,
    accentHover: Boolean = false,
    disabledVisualOpacity: Double? = null,
    content: (@Composable () -> Unit)? = null
) {
    Style(LumaticaButtonStyles)
    var isPressed by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    val effectiveHeightPx = if (driverLoginReference) heightPx else compactButtonMetric(heightPx, LUMATICA_BUTTON_MIN_HEIGHT_PX)
    val effectivePaddingH = if (driverLoginReference) paddingH else compactButtonMetric(paddingH, minValue = 8)

    val variantClass = when (variant) {
        LumaticaButtonVariant.Action  -> LumaticaButtonStyles.action
        LumaticaButtonVariant.State   -> if (isActive) LumaticaButtonStyles.stateOn else LumaticaButtonStyles.stateOff
        LumaticaButtonVariant.VertriebsAntwort -> if (isActive) LumaticaButtonStyles.vertriebsAntwortActive else LumaticaButtonStyles.vertriebsAntwort
        LumaticaButtonVariant.Standard -> LumaticaButtonStyles.standard
    }
    val pressedClass = when {
        !isPressed -> null
        variant == LumaticaButtonVariant.Action -> LumaticaButtonStyles.actionPressed
        variant == LumaticaButtonVariant.Standard -> LumaticaButtonStyles.standardPressed
        else -> null
    }

    Button(attrs = {
        classes(LumaticaButtonStyles.base, variantClass)
        if (accentHover) classes(LumaticaButtonStyles.accentHover)
        if (driverLoginReference) classes(LumaticaButtonStyles.driverLoginReference)
        if (accessPrimary) classes(LumaticaButtonStyles.accessPrimary)
        if (pressedClass != null) classes(pressedClass)
        if (!enabled) classes(LumaticaButtonStyles.btnDisabled)
        style {
            if (!enabled && disabledVisualOpacity != null) opacity(disabledVisualOpacity)
            if (wrapLabel) {
                minHeight(effectiveHeightPx.px)
                property("height", "auto")
                maxWidth(100.percent)
                property("white-space", "normal")
                property("overflow-wrap", "anywhere")
                property("line-height", "1.35")
                padding(6.px, effectivePaddingH.px)
            } else {
                height(effectiveHeightPx.px)
                padding(0.px, effectivePaddingH.px)
            }
            if (fillWidth) width(100.percent)
            justifyContent(if (variant == LumaticaButtonVariant.VertriebsAntwort) JustifyContent.Center else justify)
        }
        if (enabled) {
            onMouseDown { isPressed = true }
            onMouseUp { isPressed = false }
            onMouseLeave { isPressed = false }
            onTouchStart { isPressed = true }
            onTouchEnd { isPressed = false }
            onClick {
                if (variant != LumaticaButtonVariant.State) {
                    isPressed = true
                    scope.launch {
                        delay(220)
                        isPressed = false
                    }
                } else {
                    isPressed = false
                }
                onClick()
            }
        }
    }) {
        if (content != null) content() else Text(label)
    }
}

private fun compactButtonMetric(value: Int, minValue: Int): Int {
    if (value <= minValue) return value
    return (value * LUMATICA_BUTTON_SCALE).roundToInt().coerceAtLeast(minValue)
}
